import React, { useEffect, useState } from 'react';
import { useSuccessAlert } from '../../../App.hooks';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { ContentRequestTypeEnum, useSetRequestMutation } from '../../../generated/graphql';
import { gql, useQuery } from '@apollo/client';
import RequestTagSelector from '../../main/RequestTagSelector/RequestTagSelector';

const GET_CONTENT_TAG_IDS = gql`
    query GetContentTagIdsForRequest($id: String) {
        Content(id: $id) {
            tagIds
        }
    }
`;

type RequestContentEditDialogProps = {
    visible: boolean;
    contentId: string;
    contentName?: string | null;
    onClose: () => void;
};

const RequestContentEditDialog = ({ visible, contentId, contentName, onClose }: RequestContentEditDialogProps) => {
    const [file, setFile] = useState<File | null>(null);
    const [comment, setComment] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [tagsValid, setTagsValid] = useState(false);
    const [missingTagTypes, setMissingTagTypes] = useState<string[]>([]);
    const [submit, { loading }] = useSetRequestMutation();
    const { data: contentTagsData, loading: contentTagsLoading, error: contentTagsError } = useQuery(GET_CONTENT_TAG_IDS, {
        variables: { id: contentId },
        skip: !visible || !contentId,
        fetchPolicy: 'network-only',
    });
    const setSuccessAlert = useSuccessAlert();

    useEffect(() => {
        if (visible && contentTagsData?.Content?.tagIds) {
            setTagIds(contentTagsData.Content.tagIds.filter((id: string | null): id is string => !!id));
        }
    }, [visible, contentTagsData]);

    const handleSubmit = () => {
        if (!file) { setError('A replacement content file is required.'); return; }
        if (!comment.trim()) { setError('Please describe the requested change.'); return; }
        if (contentTagsLoading || contentTagsError) {
            setError(contentTagsError ? 'Unable to load the content tags.' : 'Content tags are still loading.');
            return;
        }
        if (!tagsValid) {
            setError(missingTagTypes.length
                ? `Select at least one tag for: ${missingTagTypes.join(', ')}.`
                : 'Tag requirements could not be validated.');
            return;
        }
        submit({
            variables: {
                request: { contentId, requestType: ContentRequestTypeEnum.EditContent, name: contentName, tagIds } as any,
                file,
                message: comment,
            },
        }).then(() => {
            setSuccessAlert('Edit Request Submitted!');
            setFile(null);
            setComment('');
            setTagIds([]);
            onClose();
        }).catch((err) => setError(err?.message || 'Unable to submit the edit request.'));
    };

    return (
        <DialogBase title="Request an Edit" type="OkCancel" visible={visible} onClose={onClose}
            onOkClick={handleSubmit} isLoading={loading}>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                Submit a proposed replacement for <strong>{contentName}</strong>. You will be assigned to the request.
            </p>
            <div className="flex flex-col gap-3">
                <input className="input-base" type="file" accept=".rvt,.rfa" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                <textarea className="input-base" rows={4} placeholder="Describe the requested change..."
                    value={comment} onChange={(e) => setComment(e.target.value)} />
                <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Tags</label>
                    <RequestTagSelector
                        value={tagIds}
                        onChange={setTagIds}
                        onValidityChange={(valid, missing) => {
                            setTagsValid(valid);
                            setMissingTagTypes(missing);
                        }}
                    />
                </div>
                {error && <span className="text-xs" style={{ color: 'var(--error, #dc2626)' }}>{error}</span>}
            </div>
        </DialogBase>
    );
};

export default RequestContentEditDialog;
